/**
 * ParentRegistration - Admin-Imported Student Signup Flow
 * 
 * FLOW 2: Admin-imported student signup
 * Follows strict step order per specification:
 * 1. View assigned classes (first view)
 * 2. Student details
 * 3. Medical information
 * 4. Guardian + contact details
 * 5. Card capture (saves card, doesn't charge)
 * 6. Account setup (email + password) - INLINE
 * 7. Direct to parent portal
 * 
 * Constraints:
 * - NO free trial steps
 * - Must begin with assigned classes
 * - Account creation happens AFTER card capture
 * - Portal access only after completion
 */

import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Toaster, toast } from 'sonner';
import { CheckCircle, AlertCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import api from '../lib/api';
import { useAuth } from '../lib/auth';

// Import refactored step components
import {
  StepIndicator,
  ClassSelectionStep,
  StudentDetailsStep,
  MedicalInfoStep,
  GuardianDetailsStep,
  CardCaptureStep,
  AccountCreationStep
} from '../components/registration';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const ParentRegistration = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  const token = searchParams.get('token');
  
  // Step state: 1=Classes, 2=Student, 3=Medical, 4=Guardian, 5=Card, 6=Account
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Data from API
  const [studentData, setStudentData] = useState(null);
  const [allStudents, setAllStudents] = useState([]); // All siblings from API
  const [hasSiblings, setHasSiblings] = useState(false);
  const [classes, setClasses] = useState([]);
  const [preSelectedClasses, setPreSelectedClasses] = useState([]);
  const [preSelectedClassIds, setPreSelectedClassIds] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [discountNote, setDiscountNote] = useState('');
  const [pendingId, setPendingId] = useState(null);
  const [cardSaved, setCardSaved] = useState(false);
  
  // Mid-term pro-rated pricing
  const [isMidTerm, setIsMidTerm] = useState(false);
  const [weeksRemaining, setWeeksRemaining] = useState(null);
  const [termLength, setTermLength] = useState(null);
  
  // Multi-student form state
  const [studentForms, setStudentForms] = useState([]); // Array of student forms
  const [currentStudentIndex, setCurrentStudentIndex] = useState(0); // Which student we're editing
  
  // Legacy single student form (for backwards compatibility)
  const [studentForm, setStudentForm] = useState({
    name: '',
    birth_date: '',
    gender: '',
    address: ''
  });
  
  // Form state - Medical (will be per-student)
  const [medicalForms, setMedicalForms] = useState([]); // Array of medical forms
  const [medicalForm, setMedicalForm] = useState({
    conditions: '',
    allergies: '',
    emergency_contact: '',
    emergency_phone: ''
  });
  
  // Form state - Guardian (shared)
  const [guardianForm, setGuardianForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    guardian2_first_name: '',
    guardian2_last_name: '',
    guardian2_email: '',
    guardian2_phone: '',
    guardian2_receives_emails: true
  });
  
  // Form state - Account
  const [accountForm, setAccountForm] = useState({
    password: '',
    confirmPassword: ''
  });

  // Idempotency: prevent double-submit at the click boundary (faster than React state),
  // and send a stable client key so the backend can detect retries.
  const submitInFlightRef = useRef(false);
  const idempotencyKeyRef = useRef(null);
  if (idempotencyKeyRef.current === null && token) {
    const storageKey = `parent_registration_idempotency_${token}`;
    let existing = null;
    try { existing = sessionStorage.getItem(storageKey); } catch (e) {}
    if (existing) {
      idempotencyKeyRef.current = existing;
    } else {
      const k = `${token}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      idempotencyKeyRef.current = k;
      try { sessionStorage.setItem(storageKey, k); } catch (e) {}
    }
  }

  // Load and verify token on mount
  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setError('No invitation token provided. Please use the link from your email.');
      setLoading(false);
    }
  }, [token]);

  // Save state to sessionStorage for recovery
  useEffect(() => {
    if (token && !loading) {
      sessionStorage.setItem(`parent_registration_${token}`, JSON.stringify({
        studentForm,
        medicalForm,
        guardianForm,
        selectedClasses,
        step,
        pendingId,
        cardSaved
      }));
    }
  }, [token, studentForm, medicalForm, guardianForm, selectedClasses, step, pendingId, cardSaved, loading]);

  const verifyToken = async () => {
    try {
      const res = await api.get(`/register/verify-invite?token=${token}`);
      const data = res.data;
      setStudentData(data.student);
      
      // Check if user is returning with existing progress
      const resume = data.resume_data;
      if (resume && resume.pending_id) {
        console.log('Resuming registration with pending_id:', resume.pending_id);
        setPendingId(resume.pending_id);
        
        // Restore form data from server
        if (resume.student_name) {
          setStudentForm(prev => ({ ...prev, name: resume.student_name }));
        }
        if (resume.guardian_email) {
          setGuardianForm(prev => ({
            ...prev,
            email: resume.guardian_email,
            first_name: resume.guardian_first_name || prev.first_name,
            last_name: resume.guardian_last_name || prev.last_name,
            phone: resume.guardian_phone || prev.phone
          }));
        }
        if (resume.medical_conditions || resume.emergency_contact) {
          setMedicalForm(prev => ({
            ...prev,
            conditions: resume.medical_conditions || prev.conditions,
            emergency_contact: resume.emergency_contact || prev.emergency_contact,
            emergency_phone: resume.emergency_phone || prev.emergency_phone
          }));
        }
        if (resume.selected_class_ids?.length > 0) {
          setSelectedClasses(resume.selected_class_ids.filter(id => !data.pre_selected_class_ids?.includes(id)));
        }
        
        // Determine which step to resume from
        if (resume.card_saved) {
          setCardSaved(true);
          setStep(6);
        } else if (resume.registration_progress === 'card_pending') {
          setStep(5);
        }
      } else {
        // New registration - pre-fill from imported student data
        setStudentForm({
          name: data.student?.student_name || '',
          birth_date: data.student?.birth_date || '',
          gender: data.student?.gender || '',
          address: data.student?.address || ''
        });
        
        if (data.student?.medical_conditions) {
          setMedicalForm(prev => ({ ...prev, conditions: data.student.medical_conditions }));
        }
        
        setGuardianForm(prev => ({
          ...prev,
          email: data.student?.guardian1_email || '',
          first_name: data.student?.guardian1_name?.split(' ')[0] || '',
          last_name: data.student?.guardian1_name?.split(' ').slice(1).join(' ') || '',
          phone: data.student?.guardian1_phone || '',
          guardian2_first_name: data.student?.guardian2_first_name || '',
          guardian2_last_name: data.student?.guardian2_last_name || '',
          guardian2_email: data.student?.guardian2_email || '',
          guardian2_phone: data.student?.guardian2_phone || ''
        }));
      }
      
      setPreSelectedClassIds(data.pre_selected_class_ids || []);
      setPreSelectedClasses(data.pre_selected_classes || []);
      setDiscountPercent(data.discount_percent || 0);
      setDiscountNote(data.discount_note || '');
      
      // Mid-term pro-rated pricing - set from API response
      setIsMidTerm(data.is_mid_term || false);
      setWeeksRemaining(data.weeks_remaining || null);
      setTermLength(data.term_length || null);
      
      // Store sibling data and initialize forms for each student
      const students = data.all_students || [];
      setAllStudents(students);
      setHasSiblings(data.has_siblings || false);
      
      // Initialize form arrays for each student
      if (students.length > 0) {
        const initStudentForms = students.map(s => ({
          id: s.id,
          name: s.student_name || '',
          birth_date: s.birth_date || '',
          gender: s.gender || '',
          address: '',
          discount_percent: s.discount_percent || 0,
          discount_note: s.discount_note || '',
          class_ids: s.class_ids || [],
          classes: s.classes || []
        }));
        setStudentForms(initStudentForms);
        
        const initMedicalForms = students.map(s => ({
          id: s.id,
          name: s.student_name || '',
          conditions: s.medical_conditions || '',
          allergies: '',
          emergency_contact: '',
          emergency_phone: ''
        }));
        setMedicalForms(initMedicalForms);
        
        // Set first student as active for legacy forms
        if (students[0]) {
          setStudentForm({
            name: students[0].student_name || '',
            birth_date: students[0].birth_date || '',
            gender: students[0].gender || '',
            address: ''
          });
          setMedicalForm({
            conditions: students[0].medical_conditions || '',
            allergies: '',
            emergency_contact: '',
            emergency_phone: ''
          });
        }
      }
      
      const classesRes = await api.get('/classes');
      setClasses(classesRes.data || []);
      
    } catch (err) {
      console.error('Token verification failed:', err);
      setError(err.response?.data?.detail || 'Unable to load registration. Please contact UPA for a new link.');
    } finally {
      setLoading(false);
    }
  };

  const handleClassToggle = (classId) => {
    setSelectedClasses(prev => 
      prev.includes(classId) 
        ? prev.filter(id => id !== classId)
        : [...prev, classId]
    );
  };

  const calculateTotal = () => {
    // Bundle discount constants
    const BUNDLE_ORIGINAL_PRICE = 54;
    const BUNDLE_DISCOUNTED_PRICE = 45;
    
    // Helper: check if a class qualifies for bundle
    const isBundleClass = (name) => {
      const lower = (name || '').toLowerCase();
      return lower.includes('ballet') || lower.includes('street dance');
    };
    
    // Helper: detect monthly subscription classes (Primatotterina etc).
    // These are billed monthly by Stripe — they have no concept of
    // "weeks remaining in term" so pro-rating must NOT apply to them.
    const isSubscriptionClass = (cls) =>
      Boolean(cls?.is_subscription) || cls?.price_period === 'month';

    // Helper function to calculate price based on mid-term pro-rating
    const getClassPrice = (cls) => {
      // Subscription classes — return the monthly subscription price as-is.
      if (isSubscriptionClass(cls)) {
        return Number(cls?.subscription_price ?? cls?.monthly_price ?? cls?.term_price ?? 0);
      }
      const fullPrice = cls?.term_price || 0;
      
      // If mid-term and we have valid weeks data, calculate pro-rated price
      if (isMidTerm && weeksRemaining && termLength && weeksRemaining < termLength) {
        const proRatedPrice = (fullPrice / termLength) * weeksRemaining;
        return Math.round(proRatedPrice * 100) / 100;
      }
      
      return fullPrice;
    };
    
    // Check for bundle eligibility across ALL pre-selected classes per student
    // Group classes by student_name to check bundle per-student
    const studentClassMap = {};
    for (const cls of preSelectedClasses) {
      const studentName = cls?.student_name || '_default';
      if (!studentClassMap[studentName]) studentClassMap[studentName] = [];
      studentClassMap[studentName].push(cls);
    }
    
    // For pre-selected classes, apply bundle + sibling discounts
    const preSelectedTotal = preSelectedClasses.reduce((total, cls) => {
      let price = getClassPrice(cls);
      
      // Check bundle for this student's classes
      const studentName = cls?.student_name || '_default';
      const studentClasses = studentClassMap[studentName] || [];
      const hasBallet = studentClasses.some(c => (c.name || '').toLowerCase().includes('ballet'));
      const hasStreet = studentClasses.some(c => (c.name || '').toLowerCase().includes('street dance'));
      const hasBundle = hasBallet && hasStreet;
      
      // Apply bundle discount (£54 -> £45)
      if (hasBundle && isBundleClass(cls.name) && (cls?.term_price || 0) === BUNDLE_ORIGINAL_PRICE) {
        const bundleRatio = BUNDLE_DISCOUNTED_PRICE / BUNDLE_ORIGINAL_PRICE;
        price = price * bundleRatio;
      }
      
      // Apply sibling discount
      const classDiscount = cls?.student_discount_percent !== undefined 
        ? cls.student_discount_percent 
        : (discountPercent || 0);
      const discountedPrice = price * (1 - classDiscount / 100);
      return total + discountedPrice;
    }, 0);
    
    // For additional classes, check bundle with pre-selected too
    const additionalTotal = selectedClasses.reduce((total, classId) => {
      const cls = classes.find(c => c.id === classId);
      let price = getClassPrice(cls);
      const additionalDiscount = hasSiblings ? 0 : (discountPercent || 0);
      const discountedPrice = price * (1 - additionalDiscount / 100);
      return total + discountedPrice;
    }, 0);
    
    // Calculate subtotal (full price before pro-rating and discount) for display.
    // Subscription classes' "full price" === their pro-rated price (no pro-rating).
    const fullPriceForCls = (cls) =>
      isSubscriptionClass(cls)
        ? Number(cls?.subscription_price ?? cls?.monthly_price ?? cls?.term_price ?? 0)
        : Number(cls?.term_price || 0);
    const fullPriceSubtotal = preSelectedClasses.reduce((total, cls) => 
      total + fullPriceForCls(cls), 0
    ) + selectedClasses.reduce((total, classId) => {
      const cls = classes.find(c => c.id === classId);
      return total + fullPriceForCls(cls);
    }, 0);
    
    // Calculate pro-rated subtotal (before discount)
    const proRatedSubtotal = preSelectedClasses.reduce((total, cls) => 
      total + getClassPrice(cls), 0
    ) + selectedClasses.reduce((total, classId) => {
      const cls = classes.find(c => c.id === classId);
      return total + getClassPrice(cls);
    }, 0);
    
    const finalTotal = preSelectedTotal + additionalTotal;
    const totalDiscount = proRatedSubtotal - finalTotal;
    const proRatingSavings = fullPriceSubtotal - proRatedSubtotal;
    
    return { 
      fullPriceSubtotal,
      subtotal: proRatedSubtotal, 
      discount: totalDiscount, 
      proRatingSavings,
      total: finalTotal,
      isMidTerm,
      weeksRemaining,
      termLength
    };
  };

  // Step 4 submission - creates pending enrollment
  const handleGuardianSubmit = async () => {
    setSubmitting(true);
    try {
      const allClassIds = [...preSelectedClassIds, ...selectedClasses];
      
      const res = await api.post('/register/submit-imported-enrollment', {
        token,
        student_name: studentForm.name,
        birth_date: studentForm.birth_date,
        gender: studentForm.gender,
        address: studentForm.address,
        medical_conditions: medicalForm.conditions,
        allergies: medicalForm.allergies,
        emergency_contact: medicalForm.emergency_contact,
        emergency_phone: medicalForm.emergency_phone,
        guardian_email: guardianForm.email,
        guardian_first_name: guardianForm.first_name,
        guardian_last_name: guardianForm.last_name,
        guardian_phone: guardianForm.phone,
        guardian2_first_name: guardianForm.guardian2_first_name,
        guardian2_last_name: guardianForm.guardian2_last_name,
        guardian2_email: guardianForm.guardian2_email,
        guardian2_phone: guardianForm.guardian2_phone,
        guardian2_receives_emails: guardianForm.guardian2_receives_emails,
        selected_class_ids: allClassIds,
        total_amount: calculateTotal().total
      });
      
      setPendingId(res.data.pending_id);
      setStep(5);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit enrollment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCardSaved = (data) => {
    setCardSaved(true);
    setStep(6);
  };

  const handleCardError = (errorType) => {
    if (errorType === 'pending_not_found') {
      setPendingId(null);
      setStep(4);
    }
  };

  const handleCreateAccount = async () => {
    // Idempotency guard — block double-clicks before React state can re-render
    if (submitInFlightRef.current) {
      return;
    }
    submitInFlightRef.current = true;

    if (!pendingId) {
      submitInFlightRef.current = false;
      toast.error('Session expired. Please start the registration again.');
      sessionStorage.removeItem(`parent_registration_${token}`);
      setStep(1);
      return;
    }
    
    if (!accountForm.password || accountForm.password.length < 8) {
      submitInFlightRef.current = false;
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (accountForm.password !== accountForm.confirmPassword) {
      submitInFlightRef.current = false;
      toast.error('Passwords do not match');
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post('/register/complete-imported-account', {
        pending_id: pendingId,
        token,
        email: guardianForm.email,
        password: accountForm.password,
        first_name: guardianForm.first_name,
        last_name: guardianForm.last_name,
        // Pass student forms for siblings
        student_forms: studentForms,
        medical_forms: medicalForms,
        // Server-side idempotency token
        idempotency_key: idempotencyKeyRef.current
      });
      
      sessionStorage.removeItem(`parent_registration_${token}`);

      // GTM conversion event — only when the account was genuinely created now
      if (!res.data.already_completed) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'registration_complete' });
      }
      
      if (res.data.token) {
        setAuth(res.data.token, res.data.user);
        const studentCount = res.data.student_count || 1;
        toast.success(`Account created! ${studentCount > 1 ? `${studentCount} students registered.` : ''} Welcome to UPA!`);
        navigate('/parent');
      } else {
        setSuccess(true);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to create account';
      toast.error(errorMsg);
      
      if (err.response?.status === 404) {
        toast.error('Registration session expired. Please restart.');
        sessionStorage.removeItem(`parent_registration_${token}`);
        setPendingId(null);
        setCardSaved(false);
        setStep(4);
      }
      // Allow retry only on actual error
      submitInFlightRef.current = false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleSessionExpired = () => {
    sessionStorage.removeItem(`parent_registration_${token}`);
    window.location.reload();
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#FF007F] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/60">Verifying your invitation...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Navbar />
        <div className="max-w-md mx-auto px-4 py-20">
          <Card className="bg-red-500/10 border-red-500/30">
            <CardContent className="p-8 text-center">
              <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-white mb-2">Invalid Invitation</h2>
              <p className="text-white/60 mb-6">{error}</p>
              <Button 
                onClick={() => navigate('/')}
                className="bg-[#FF007F] hover:bg-[#D6006A] text-white"
              >
                Go to Homepage
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Navbar />
        <div className="max-w-md mx-auto px-4 py-20 text-center">
          <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4">Registration Complete!</h1>
          <p className="text-white/60 mb-8">
            Welcome to Universal Performing Arts! Your enrollment has been submitted.
          </p>
          <Button 
            className="bg-[#FF007F] hover:bg-[#D6006A] text-white"
            onClick={() => navigate('/parent')}
          >
            Go to Parent Portal
          </Button>
        </div>
      </div>
    );
  }

  const totals = calculateTotal();

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="parent-registration-page">
      <Navbar />
      <Toaster position="top-right" richColors />
      
      {/* Progress Header */}
      <div className="bg-gradient-to-b from-[#1a1a2e] to-[#0a0a0a] border-b border-white/10">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <div className="text-center mb-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">Welcome to UPA!</h1>
            <p className="text-white/60">Complete your registration for {studentData?.student_name}</p>
          </div>
          <StepIndicator currentStep={step} />
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Step 1: Classes */}
        {step === 1 && (
          <ClassSelectionStep
            preSelectedClasses={preSelectedClasses}
            preSelectedClassIds={preSelectedClassIds}
            classes={classes}
            selectedClasses={selectedClasses}
            onClassToggle={handleClassToggle}
            discountPercent={discountPercent}
            discountNote={discountNote}
            studentName={hasSiblings ? 'your children' : studentForm.name}
            allStudents={allStudents}
            hasSiblings={hasSiblings}
            calculateTotal={calculateTotal}
            onNext={() => setStep(2)}
            isMidTerm={isMidTerm}
            weeksRemaining={weeksRemaining}
            termLength={termLength}
          />
        )}

        {/* Step 2: Student Details */}
        {step === 2 && (
          <StudentDetailsStep
            studentForm={studentForm}
            setStudentForm={setStudentForm}
            studentForms={studentForms}
            setStudentForms={setStudentForms}
            allStudents={allStudents}
            hasSiblings={hasSiblings}
            onNext={() => setStep(3)}
            onBack={() => setStep(1)}
          />
        )}

        {/* Step 3: Medical Information */}
        {step === 3 && (
          <MedicalInfoStep
            medicalForm={medicalForm}
            setMedicalForm={setMedicalForm}
            medicalForms={medicalForms}
            setMedicalForms={setMedicalForms}
            allStudents={allStudents}
            hasSiblings={hasSiblings}
            onNext={() => setStep(4)}
            onBack={() => setStep(2)}
          />
        )}

        {/* Step 4: Guardian Details */}
        {step === 4 && (
          <GuardianDetailsStep
            guardianForm={guardianForm}
            setGuardianForm={setGuardianForm}
            onNext={handleGuardianSubmit}
            onBack={() => setStep(3)}
            submitting={submitting}
          />
        )}

        {/* Step 5: Card Capture */}
        {step === 5 && (
          <CardCaptureStep
            stripePromise={stripePromise}
            pendingId={pendingId}
            studentName={studentForm.name}
            totalClasses={preSelectedClasses.length + selectedClasses.length}
            totalAmount={totals.total}
            onSuccess={handleCardSaved}
            onBack={() => setStep(4)}
            onError={handleCardError}
          />
        )}

        {/* Step 6: Account Creation */}
        {step === 6 && (
          <AccountCreationStep
            pendingId={pendingId}
            token={token}
            guardianEmail={guardianForm.email}
            accountForm={accountForm}
            setAccountForm={setAccountForm}
            onSubmit={handleCreateAccount}
            submitting={submitting}
            onSessionExpired={handleSessionExpired}
          />
        )}
      </div>
    </div>
  );
};

export default ParentRegistration;
